import { __decorate, __param } from '../../../../../../../external/tslib/tslib.es6.js';
import { Disposable } from '../../../../base/common/lifecycle.js';
import { ContextKeyExpr } from '../../../../platform/contextkey/common/contextkey.js';
import { IInstantiationService } from '../../../../platform/instantiation/common/instantiation.js';
import { IAccessibleViewService } from '../../accessibility/browser/accessibleView.service.js';
import { AccessibilityHelpAction } from '../../accessibility/browser/accessibleViewActions.js';
import { ctxCommentEditorFocused } from './simpleCommentEditor.js';
import { CommentContextKeys } from '../common/commentContextKeys.js';
import { localizeWithPath } from '../../../../nls.js';
import { IKeybindingService } from '../../../../platform/keybinding/common/keybinding.service.js';
import { format } from '../../../../base/common/strings.js';
import { getActiveElement } from '../../../../base/browser/dom.js';
import { ToggleTabFocusModeAction } from '../../../../editor/contrib/toggleTabFocusMode/browser/toggleTabFocusMode.js';
const _moduleId = "vs/workbench/contrib/comments/browser/commentsAccessibility";
var CommentAccessibilityHelpNLS;
( ((function(CommentAccessibilityHelpNLS) {
    CommentAccessibilityHelpNLS.intro = ( localizeWithPath(
        _moduleId,
        0,
        "The editor contains commentable range(s). Some useful commands include:"
    ));
    CommentAccessibilityHelpNLS.introWidget = ( localizeWithPath(
        _moduleId,
        1,
        "This widget contains a text area, for composition of new comments, and actions, that can be tabbed to once tab moves focus mode has been enabled ({0})."
    ));
    CommentAccessibilityHelpNLS.introWidgetNoKb = ( localizeWithPath(
        _moduleId,
        2,
        "This widget contains a text area, for composition of new comments, and actions, that can be tabbed to once tab moves focus mode has been enabled with the command Toggle Tab Key Moves Focus, which is currently not triggerable via keybinding."
    ));
    CommentAccessibilityHelpNLS.commentCommands = ( localizeWithPath(_moduleId, 3, "Some useful comment commands include:"));
    CommentAccessibilityHelpNLS.escape = ( localizeWithPath(_moduleId, 4, "- Dismiss Comment (Escape)"));
    CommentAccessibilityHelpNLS.nextRange = ( localizeWithPath(_moduleId, 5, "- Go to Next Commenting Range ({0})"));
    CommentAccessibilityHelpNLS.nextRangeNoKb = ( localizeWithPath(
        _moduleId,
        6,
        "- Go to Next Commenting Range, which is currently not triggerable via keybinding."
    ));
    CommentAccessibilityHelpNLS.previousRange = ( localizeWithPath(_moduleId, 7, "- Go to Previous Commenting Range ({0})"));
    CommentAccessibilityHelpNLS.previousRangeNoKb = ( localizeWithPath(
        _moduleId,
        8,
        "- Go to Previous Commenting Range, which is currently not triggerable via keybinding."
    ));
    CommentAccessibilityHelpNLS.nextCommentThreadKb = ( localizeWithPath(_moduleId, 9, "- Go to Next Comment Thread ({0})"));
    CommentAccessibilityHelpNLS.nextCommentThreadNoKb = ( localizeWithPath(
        _moduleId,
        10,
        "- Go to Next Comment Thread, which is currently not triggerable via keybinding."
    ));
    CommentAccessibilityHelpNLS.previousCommentThreadKb = ( localizeWithPath(_moduleId, 11, "- Go to Previous Comment Thread ({0})"));
    CommentAccessibilityHelpNLS.previousCommentThreadNoKb = ( localizeWithPath(
        _moduleId,
        12,
        "- Go to Previous Comment Thread, which is currently not triggerable via keybinding."
    ));
    CommentAccessibilityHelpNLS.addComment = ( localizeWithPath(_moduleId, 13, "- Add Comment ({0})"));
    CommentAccessibilityHelpNLS.addCommentNoKb = ( localizeWithPath(
        _moduleId,
        14,
        "- Add Comment on Current Selection, which is currently not triggerable via keybinding."
    ));
    CommentAccessibilityHelpNLS.submitComment = ( localizeWithPath(_moduleId, 15, "- Submit Comment ({0})"));
    CommentAccessibilityHelpNLS.submitCommentNoKb = ( localizeWithPath(
        _moduleId,
        16,
        "- Submit Comment, accessible via tabbing, as it's currently not triggerable with a keybinding."
    ));
})(CommentAccessibilityHelpNLS || (CommentAccessibilityHelpNLS = {}))));
let CommentsAccessibilityHelpProvider = class CommentsAccessibilityHelpProvider {
    constructor(_keybindingService) {
        this._keybindingService = _keybindingService;
        this.id = "comments" ;
        this.verbositySettingKey = "accessibility.verbosity.comments" ;
        this.options = { type: "help"  };
    }
    _descriptionForCommand(commandId, msg, noKbMsg) {
        const kb = this._keybindingService.lookupKeybinding(commandId);
        if (kb) {
            return format(msg, kb.getAriaLabel());
        }
        return format(noKbMsg, commandId);
    }
    provideContent() {
        this._element = getActiveElement();
        const content = [];
        content.push(this._descriptionForCommand(ToggleTabFocusModeAction.ID, CommentAccessibilityHelpNLS.introWidget, CommentAccessibilityHelpNLS.introWidgetNoKb) + '\n');
        content.push(CommentAccessibilityHelpNLS.commentCommands);
        content.push(CommentAccessibilityHelpNLS.escape);
        content.push(this._descriptionForCommand("workbench.action.addComment" , CommentAccessibilityHelpNLS.addComment, CommentAccessibilityHelpNLS.addCommentNoKb));
        content.push(this._descriptionForCommand("editor.action.submitComment" , CommentAccessibilityHelpNLS.submitComment, CommentAccessibilityHelpNLS.submitCommentNoKb));
        content.push(this._descriptionForCommand("editor.action.nextCommentingRange" , CommentAccessibilityHelpNLS.nextRange, CommentAccessibilityHelpNLS.nextRangeNoKb));
        content.push(this._descriptionForCommand("editor.action.previousCommentingRange" , CommentAccessibilityHelpNLS.previousRange, CommentAccessibilityHelpNLS.previousRangeNoKb));
        return content.join('\n');
    }
    onClose() {
        this._element?.focus();
    }
};
CommentsAccessibilityHelpProvider = ( (__decorate([
    ( (__param(0, IKeybindingService)))
], CommentsAccessibilityHelpProvider)));
class CommentsAccessibilityHelpContribution extends Disposable {
    constructor() {
        super();
        this._register(AccessibilityHelpAction.addImplementation(110, 'comments', accessor => {
            const instantiationService = accessor.get(IInstantiationService);
            const accessibleViewService = accessor.get(IAccessibleViewService);
            accessibleViewService.show(instantiationService.createInstance(CommentsAccessibilityHelpProvider));
            return true;
        }, ( (ContextKeyExpr.or(ctxCommentEditorFocused, CommentContextKeys.commentFocused)))));
    }
}
export { CommentAccessibilityHelpNLS, CommentsAccessibilityHelpContribution, CommentsAccessibilityHelpProvider };
